// src/services/nwsWeatherService.js
// NWS天气预警服务 - 简单实用版

// 🔧 NWS API配置
const NWS_CONFIG = {
  baseURL: 'https://api.weather.gov',
  // Brooklyn & Queens的主要区域代码
  areas: ['NYC'], // New York City包含所有区
  // 我们关心的洪水相关警报类型
  floodEvents: [
    'Flood Warning',
    'Flash Flood Warning', 
    'Flood Watch',
    'Flash Flood Watch',
    'Coastal Flood Warning',
    'Coastal Flood Watch'
  ]
};

// 🎨 预警颜色配置
const ALERT_COLORS = {
  'Flood Watch': '#fbbf24',           // 黄色 - 观察
  'Flash Flood Watch': '#fbbf24',     // 黄色 - 观察
  'Coastal Flood Watch': '#fbbf24',   // 黄色 - 观察
  'Flood Warning': '#f97316',         // 橙色 - 警告
  'Coastal Flood Warning': '#f97316', // 橙色 - 警告
  'Flash Flood Warning': '#dc2626',   // 红色 - 紧急
};

// 🔧 简化的预警数据处理
class NWSWeatherService {
  constructor() {
    this.cache = new Map();
    this.lastFetch = 0;
    this.cacheDuration = 10 * 60 * 1000; // 10分钟缓存
  }

  // 🌐 获取NYC区域的洪水预警
  async getFloodAlerts() {
    try {
      // 检查缓存
      const now = Date.now();
      if (this.cache.has('alerts') && (now - this.lastFetch) < this.cacheDuration) {
        return this.cache.get('alerts');
      }

      console.log('🌦️ Fetching NWS flood alerts for NYC...');
      
      // 获取NYC区域活跃警报
      const response = await fetch(`${NWS_CONFIG.baseURL}/alerts/active?area=NYC`, {
        headers: {
          'User-Agent': 'FloodMapNYC/1.0 (github.com/your-repo)',
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`NWS API error: ${response.status}`);
      }

      const data = await response.json();
      const processedAlerts = this.processAlerts(data.features || []);
      
      // 更新缓存
      this.cache.set('alerts', processedAlerts);
      this.lastFetch = now;
      
      console.log(`✅ Found ${processedAlerts.length} flood-related alerts`);
      return processedAlerts;

    } catch (error) {
      console.error('❌ NWS API fetch failed:', error);
      // 返回缓存数据或空数组
      return this.cache.get('alerts') || [];
    }
  }

  // 📋 处理NWS警报数据
  processAlerts(features) {
    return features
      .filter(feature => {
        const event = feature.properties?.event;
        return event && NWS_CONFIG.floodEvents.includes(event);
      })
      .map(feature => {
        const props = feature.properties;
        return {
          id: props.id,
          event: props.event,
          headline: props.headline,
          description: props.description,
          severity: props.severity || 'Unknown',
          urgency: props.urgency || 'Unknown',
          areas: props.areaDesc || '',
          effective: props.effective,
          expires: props.expires,
          sent: props.sent,
          color: this.getAlertColor(props.event),
          geometry: feature.geometry, // 可能包含警报区域的地理信息
          // 简化的用户友好信息
          userMessage: this.generateUserMessage(props),
          isActive: this.isAlertActive(props),
          affectsBrooklynQueens: this.checkBrooklynQueens(props.areaDesc)
        };
      })
      .filter(alert => alert.isActive && alert.affectsBrooklynQueens);
  }

  // 🎨 获取警报颜色
  getAlertColor(eventType) {
    return ALERT_COLORS[eventType] || '#6b7280'; // 默认灰色
  }

  // ✅ 检查警报是否仍然有效
  isAlertActive(props) {
    const now = new Date();
    const expires = new Date(props.expires);
    return expires > now;
  }

  // 📍 检查是否影响Brooklyn或Queens
  checkBrooklynQueens(areaDesc) {
    if (!areaDesc) return false;
    const desc = areaDesc.toLowerCase();
    return desc.includes('brooklyn') || 
           desc.includes('queens') || 
           desc.includes('new york city') ||
           desc.includes('nyc');
  }

  // 💬 生成用户友好的消息
  generateUserMessage(props) {
    const event = props.event;
    const expires = new Date(props.expires);
    const timeString = expires.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit'
    });

    const messages = {
      'Flood Watch': `Flooding possible. Monitor conditions until ${timeString}.`,
      'Flash Flood Watch': `Flash flooding possible. Stay alert until ${timeString}.`,
      'Coastal Flood Watch': `Coastal flooding possible. Avoid low areas until ${timeString}.`,
      'Flood Warning': `Flooding expected! Avoid low-lying areas until ${timeString}.`,
      'Coastal Flood Warning': `Coastal flooding occurring! Stay away from shore until ${timeString}.`,
      'Flash Flood Warning': `⚠️ FLASH FLOOD EMERGENCY! Seek higher ground immediately!`
    };

    return messages[event] || `Weather alert active until ${timeString}.`;
  }

  // 📊 获取当前最高风险等级
  getCurrentRiskLevel(alerts) {
    if (!alerts || alerts.length === 0) return 'safe';
    
    // 按严重程度排序
    const severityOrder = {
      'Flash Flood Warning': 4,
      'Flood Warning': 3,
      'Coastal Flood Warning': 3,
      'Flash Flood Watch': 2,
      'Flood Watch': 1,
      'Coastal Flood Watch': 1
    };

    const maxSeverity = Math.max(...alerts.map(alert => severityOrder[alert.event] || 0));
    
    if (maxSeverity >= 4) return 'emergency';
    if (maxSeverity >= 3) return 'warning'; 
    if (maxSeverity >= 2) return 'watch';
    return 'safe';
  }

  // 🏷️ 获取风险等级的显示信息
  getRiskLevelInfo(level) {
    const info = {
      safe: {
        color: '#10b981',
        icon: '✅',
        title: 'No Weather Alerts',
        message: 'Current conditions are safe for travel.'
      },
      watch: {
        color: '#fbbf24', 
        icon: '🟡',
        title: 'Flood Watch',
        message: 'Flooding possible. Monitor conditions.'
      },
      warning: {
        color: '#f97316',
        icon: '⚠️', 
        title: 'Flood Warning',
        message: 'Flooding expected. Avoid low areas.'
      },
      emergency: {
        color: '#dc2626',
        icon: '🚨',
        title: 'Flash Flood Emergency',
        message: 'IMMEDIATE DANGER! Seek higher ground!'
      }
    };

    return info[level] || info.safe;
  }

  // 🔄 清除缓存（用于强制刷新）
  clearCache() {
    this.cache.clear();
    this.lastFetch = 0;
  }
}

// 🌐 导出单例实例
export const nwsWeatherService = new NWSWeatherService();

// 🔧 React Hook for easy integration
import { useState, useEffect, useCallback } from 'react';

export const useNWSAlerts = () => {
  const [alerts, setAlerts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [riskLevel, setRiskLevel] = useState('safe');

  const fetchAlerts = useCallback(async () => {
    setIsLoading(true);
    try {
      const newAlerts = await nwsWeatherService.getFloodAlerts();
      setAlerts(newAlerts);
      setRiskLevel(nwsWeatherService.getCurrentRiskLevel(newAlerts));
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Failed to fetch NWS alerts:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // 自动获取数据
  useEffect(() => {
    fetchAlerts();
    
    // 每10分钟自动更新
    const interval = setInterval(fetchAlerts, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchAlerts]);

  return {
    alerts,
    riskLevel,
    riskLevelInfo: nwsWeatherService.getRiskLevelInfo(riskLevel),
    isLoading,
    lastUpdated,
    refresh: fetchAlerts
  };
};

export default nwsWeatherService;